import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
} from '@munifocus/ui';
import { useEnforcedLaws } from '../../lib/enforced-laws-api';
import type { FileForm } from './useFileForm';

/**
 * Which law this file is being enforced under.
 *
 * Distinct from the by-law *category* above it, and both are needed. The
 * category routes the file into a workflow — Dangerous & Unsightly has a stage
 * machine, parking does not — while this names the instrument the municipality
 * is acting under, with its citation, for the documents and the court docket.
 * Until this existed the docket derived its statute from the category through a
 * map whose every entry read "Local Governance Act", so a file enforced under
 * anything else was filed to court naming the wrong statute.
 *
 * The options depend on the file's local government: provincial statutes
 * everywhere, a municipality's own by-laws only there. Re-anchoring a file
 * therefore changes what is on offer, and a law that is no longer available is
 * cleared rather than left sitting invisibly on the record — see below.
 */
export function EnforcedLawPicker({
  form,
  disabled = false,
}: {
  form: FileForm;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { values, set } = form;
  const laws = useEnforcedLaws(values.localGovernmentId);

  const options = laws.data ?? [];
  const provincial = options.filter((law) => law.provincial);
  const municipal = options.filter((law) => !law.provincial);

  // Re-anchoring the file can strip the chosen law out of the available set —
  // a municipal by-law does not follow a file to another municipality. Clearing
  // it is the honest outcome: the alternative is a select rendering blank while
  // still holding a value the API would reject on save, which reads to the
  // officer as the form losing their answer for no reason.
  const chosenStillOffered = options.some((law) => law.id === values.enforcedLawId);
  useEffect(() => {
    if (laws.isLoading || laws.isError) return;
    if (values.enforcedLawId && !chosenStillOffered) set('enforcedLawId', '');
  }, [laws.isLoading, laws.isError, values.enforcedLawId, chosenStillOffered, set]);

  return (
    <Stack spacing={1}>
      <FormControl fullWidth size="small" disabled={disabled || laws.isLoading}>
        <InputLabel>{t('gov.file.enforcedLaw')}</InputLabel>
        <Select
          label={t('gov.file.enforcedLaw')}
          aria-label={t('gov.file.enforcedLaw')}
          value={chosenStillOffered ? values.enforcedLawId : ''}
          onChange={(e) => set('enforcedLawId', e.target.value)}
        >
          {/* A real answer, not a placeholder: plenty of files are worked
              without anyone needing to name the statute, and the docket falls
              back to its category label for them. */}
          <MenuItem value="">{t('gov.file.enforcedLawNone')}</MenuItem>

          {provincial.length > 0 && (
            <ListSubheader>{t('gov.file.enforcedLawProvincial')}</ListSubheader>
          )}
          {provincial.map((law) => (
            <MenuItem key={law.id} value={law.id}>
              {law.citation ? `${law.name} — ${law.citation}` : law.name}
            </MenuItem>
          ))}

          {municipal.length > 0 && (
            <ListSubheader>{t('gov.file.enforcedLawMunicipal')}</ListSubheader>
          )}
          {municipal.map((law) => (
            <MenuItem key={law.id} value={law.id}>
              {law.citation ? `${law.name} — ${law.citation}` : law.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {laws.isError && <Alert severity="error">{t('gov.file.enforcedLawLoadError')}</Alert>}

      {/* An unanchored file sees the provincial statutes only. Saying so beats
          letting the officer wonder where their municipality's by-laws went. */}
      {!laws.isLoading && !laws.isError && values.localGovernmentId === '' && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.file.enforcedLawUnanchoredHint')}
        </Alert>
      )}
    </Stack>
  );
}
